import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { MEMBERS } from "@/constants/testIds";
import { Plus, Pencil, Trash2, Search, Users as UsersIcon, Download, FileSpreadsheet } from "lucide-react";
import { API } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = ["Activo", "Inactivo", "Visitante"];
const MINISTRIES = [
  "Alabanza",
  "Niños",
  "Jóvenes",
  "Ujieres",
  "Intercesión",
  "Damas",
  "Líder de Célula",
  "Danza",
  "Koinonía",
  "Board",
  "Ninguno",
];
const DEMOGRAPHICS = [
  { value: "varón", label: "Varón" },
  { value: "mujer", label: "Mujer" },
  { value: "joven", label: "Joven" },
  { value: "niño", label: "Niño" },
];

const EMPTY = {
  name: "",
  status: "Activo",
  ministry: "Ninguno",
  demographic_category: "varón",
  join_date: new Date().toISOString().slice(0, 10),
  birthdate: "",
  email: "",
  phone: "",
  notes: "",
  cell_id: "",
};

export default function Members() {
  const [items, setItems] = useState([]);
  const [cells, setCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [membersRes, cellsRes] = await Promise.all([
        api.get("/members"),
        api.get("/cells"),
      ]);
      setItems(membersRes.data);
      setCells(cellsRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setDraft(EMPTY);
    setEditingId(null);
    setError("");
    setOpen(true);
  };

  const openEdit = (m) => {
    setDraft({
      name: m.name,
      status: m.status,
      ministry: m.ministry,
      demographic_category: m.demographic_category || "varón",
      join_date: m.join_date,
      birthdate: m.birthdate || "",
      email: m.email || "",
      phone: m.phone || "",
      notes: m.notes || "",
      cell_id: m.cell_id || "",
    });
    setEditingId(m.id);
    setError("");
    setOpen(true);
  };

  const save = async () => {
    if (!draft.name.trim()) {
      setError("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/members/${editingId}`, draft);
      } else {
        await api.post("/members", draft);
      }
      setOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    await api.delete(`/members/${confirmDelete.id}`);
    setConfirmDelete(null);
    await load();
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q) ||
        (m.ministry || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  const stats = useMemo(() => {
    const count = (cat) => items.filter((m) => m.demographic_category === cat).length;
    return {
      varones: count("varón"),
      mujeres: count("mujer"),
      jovenes: count("joven"),
      ninos: count("niño"),
    };
  }, [items]);

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("ajar_token");
      const res = await fetch(`${API}/members/report.pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Error al generar PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `miembros-ajar-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "No se pudo descargar el PDF");
    }
  };

  const downloadExcel = async () => {
    try {
      const token = localStorage.getItem("ajar_token");
      const res = await fetch(`${API}/export/members.xlsx`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Error al generar Excel");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `miembros-ajar-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "No se pudo descargar el Excel");
    }
  };

  return (
    <div data-testid={MEMBERS.page}>
      <PageHeader
        title="Miembros"
        subtitle="Directorio de la congregación · ministerios y fechas de ingreso"
        actions={
          <div className="flex items-center gap-2">
            <button
              data-testid={MEMBERS.downloadPdfButton}
              onClick={downloadPdf}
              className="flex items-center gap-2 bg-white border border-[#1c2a3a] text-[#1c2a3a] hover:bg-[#1c2a3a] hover:text-[#e7c879] px-5 py-2.5 text-xs uppercase tracking-widest transition"
            >
              <Download size={14} /> Descargar PDF
            </button>
            <button
              data-testid={MEMBERS.downloadExcelButton}
              onClick={downloadExcel}
              className="flex items-center gap-2 bg-white border border-[#c8a04a] text-[#7a5b0f] hover:bg-[#fdf7e1] px-5 py-2.5 text-xs uppercase tracking-widest transition"
            >
              <FileSpreadsheet size={14} /> Excel
            </button>
            <button
              data-testid={MEMBERS.addButton}
              onClick={openNew}
              className="flex items-center gap-2 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] px-5 py-2.5 text-xs uppercase tracking-widest"
            >
              <Plus size={14} /> Nuevo miembro
            </button>
          </div>
        }
      />

      <div className="p-8 space-y-6">
        {/* Stats - Demographics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testId={MEMBERS.statVarones} label="Varones" value={stats.varones} accent="#1c2a3a" />
          <StatCard testId={MEMBERS.statMujeres} label="Mujeres" value={stats.mujeres} accent="#a64a7a" />
          <StatCard testId={MEMBERS.statJovenes} label="Jóvenes" value={stats.jovenes} accent="#3f7c2a" />
          <StatCard testId={MEMBERS.statNinos} label="Niños" value={stats.ninos} accent="#c8a04a" />
        </div>

        {/* Search */}
        <div className="bg-white border border-[#d9cfb8] px-4 py-3 flex items-center gap-3" style={{ borderTop: "3px solid #c8a04a" }}>
          <Search size={16} className="text-[#8b7e5e]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre, correo o ministerio..."
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#a89e85]"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="text-xs text-[#8b7e5e] hover:text-[#1c1a14] uppercase tracking-widest"
            >
              Limpiar
            </button>
          )}
        </div>

        {/* Cards grid */}
        {loading ? (
          <div className="text-center py-16 text-[#8b7e5e]">Cargando...</div>
        ) : filtered.length === 0 ? (
          <EmptyState hasQuery={!!query} onAdd={openNew} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((m) => (
              <MemberCard
                key={m.id}
                member={m}
                onEdit={() => openEdit(m)}
                onDelete={() => setConfirmDelete(m)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[640px] bg-[#fbf7ee] border-[#d9cfb8]" style={{ borderTop: "4px solid #c8a04a" }}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a14]">
              {editingId ? "Editar miembro" : "Nuevo miembro"}
            </DialogTitle>
            <DialogDescription className="text-[#5d5849]">
              Completa la información del miembro de la congregación.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <Field label="Nombre completo *" className="md:col-span-2">
              <input
                data-testid={MEMBERS.nameInput}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className={inputCls}
                placeholder="Ej. María González Rodríguez"
                autoFocus
              />
            </Field>

            <Field label="Estado">
              <select
                data-testid={MEMBERS.statusSelect}
                value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                className={inputCls}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>

            <Field label="Ministerio">
              <select
                data-testid={MEMBERS.ministrySelect}
                value={draft.ministry}
                onChange={(e) => setDraft({ ...draft, ministry: e.target.value })}
                className={inputCls}
              >
                {MINISTRIES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Field>

            <Field label="Categoría demográfica">
              <select
                data-testid={MEMBERS.demographicSelect}
                value={draft.demographic_category}
                onChange={(e) => setDraft({ ...draft, demographic_category: e.target.value })}
                className={inputCls}
              >
                {DEMOGRAPHICS.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Célula">
              <select
                data-testid={MEMBERS.cellSelect}
                value={draft.cell_id}
                onChange={(e) => setDraft({ ...draft, cell_id: e.target.value })}
                className={inputCls}
              >
                <option value="">— Sin asignar —</option>
                {cells.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.leader_name ? ` · ${c.leader_name}` : ""}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Fecha de ingreso">
              <input
                data-testid={MEMBERS.joinDateInput}
                type="date"
                value={draft.join_date}
                onChange={(e) => setDraft({ ...draft, join_date: e.target.value })}
                className={inputCls}
              />
            </Field>

            <Field label="Fecha de nacimiento">
              <input
                data-testid={MEMBERS.birthdateInput}
                type="date"
                value={draft.birthdate}
                onChange={(e) => setDraft({ ...draft, birthdate: e.target.value })}
                className={inputCls}
              />
            </Field>

            <Field label="Teléfono">
              <input
                data-testid={MEMBERS.phoneInput}
                value={draft.phone}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                className={inputCls}
                placeholder="Ej. 555-1234"
              />
            </Field>

            <Field label="Correo electrónico" className="md:col-span-2">
              <input
                data-testid={MEMBERS.emailInput}
                type="email"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                className={inputCls}
                placeholder="correo@ejemplo.com"
              />
            </Field>

            <Field label="Notas" className="md:col-span-2">
              <textarea
                data-testid={MEMBERS.notesInput}
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                className={inputCls + " min-h-[80px] resize-y"}
                placeholder="Observaciones, dirección, familia, etc."
              />
            </Field>
          </div>

          {error && (
            <div className="mb-2 px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <DialogFooter>
            <button
              data-testid={MEMBERS.cancelButton}
              onClick={() => setOpen(false)}
              className="px-5 py-2.5 border border-[#d9cfb8] bg-white text-[#5d5849] text-xs uppercase tracking-widest hover:bg-[#fbf7ee]"
            >
              Cancelar
            </button>
            <button
              data-testid={MEMBERS.saveButton}
              onClick={save}
              disabled={saving}
              className="px-5 py-2.5 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] text-xs uppercase tracking-widest disabled:opacity-60"
            >
              {saving ? "Guardando..." : editingId ? "Guardar cambios" : "Crear miembro"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#fbf7ee] border-[#d9cfb8]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif">Eliminar miembro</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que deseas eliminar a <strong>{confirmDelete?.name}</strong>? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-700 hover:bg-red-800 text-white">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-[#d9cfb8] px-3 py-2.5 text-sm text-[#1c1a14] focus:outline-none focus:border-[#c8a04a] focus:ring-1 focus:ring-[#c8a04a]";

function Field({ label, className = "", children }) {
  return (
    <div className={className}>
      <label className="block text-[11px] uppercase tracking-widest text-[#5d5849] mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

function StatCard({ label, value, accent, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-[#d9cfb8] p-4" style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">{label}</div>
      <div className="text-3xl font-serif text-[#1c1a14] mt-1">{value}</div>
    </div>
  );
}

function MemberCard({ member, onEdit, onDelete }) {
  const statusColor = {
    Activo: "bg-[#d6ebcb] text-[#2a5a1c] border-[#9ec88a]",
    Inactivo: "bg-[#e3e3e3] text-[#5d5849] border-[#bcbcbc]",
    Visitante: "bg-[#fdecc8] text-[#7a5b0f] border-[#e7c879]",
  };
  const initials = member.name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("");

  return (
    <div
      data-testid={MEMBERS.row(member.id)}
      className="bg-white border border-[#d9cfb8] p-5 hover:border-[#c8a04a] transition group"
      style={{ borderTop: "3px solid #c8a04a" }}
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-[#1c2a3a] text-[#e7c879] flex items-center justify-center font-serif text-base shrink-0">
          {initials || <UsersIcon size={18} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {member.custom_id && (
              <span className="font-mono text-[11px] tracking-widest text-[#c8a04a] bg-[#fbf7ee] border border-[#e7c879] px-1.5 py-0.5">
                #{member.custom_id}
              </span>
            )}
            <span className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">
              {member.demographic_category || "—"}
            </span>
          </div>
          <div className="font-serif text-lg text-[#1c1a14] leading-tight truncate mt-1">
            {member.name}
          </div>
          <div className="text-xs text-[#8b7e5e] mt-0.5">{member.ministry}</div>
          <div className="mt-2">
            <span
              className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border ${
                statusColor[member.status] || statusColor.Inactivo
              }`}
            >
              {member.status}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-1.5 text-sm text-[#5d5849]">
        {member.email && (
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-widest text-[#8b7e5e] w-16 shrink-0">Email</span>
            <span className="truncate">{member.email}</span>
          </div>
        )}
        {member.phone && (
          <div className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-widest text-[#8b7e5e] w-16 shrink-0">Tel.</span>
            <span>{member.phone}</span>
          </div>
        )}
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-widest text-[#8b7e5e] w-16 shrink-0">Ingreso</span>
          <span>{member.join_date}</span>
        </div>
        {member.notes && (
          <div className="text-xs text-[#8b7e5e] mt-2 line-clamp-2">{member.notes}</div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-[#ece4cf] flex items-center gap-2">
        <button
          data-testid={MEMBERS.editButton(member.id)}
          onClick={onEdit}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-[#d9cfb8] hover:bg-[#fbf7ee] text-[#5d5849] text-xs uppercase tracking-widest"
        >
          <Pencil size={12} /> Editar
        </button>
        <button
          data-testid={MEMBERS.deleteButton(member.id)}
          onClick={onDelete}
          className="px-3 py-2 border border-red-200 hover:bg-red-50 text-red-700"
          aria-label="Eliminar"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function EmptyState({ hasQuery, onAdd }) {
  return (
    <div className="bg-white border border-dashed border-[#d9cfb8] p-16 text-center">
      <UsersIcon className="mx-auto text-[#c8a04a]" size={36} />
      <h3 className="font-serif text-xl text-[#1c1a14] mt-4">
        {hasQuery ? "Sin resultados" : "Aún no hay miembros"}
      </h3>
      <p className="text-sm text-[#5d5849] mt-2 max-w-md mx-auto">
        {hasQuery
          ? "Ajusta la búsqueda o agrega un nuevo miembro."
          : "Comienza agregando el primer miembro de la congregación."}
      </p>
      {!hasQuery && (
        <button
          onClick={onAdd}
          className="mt-6 inline-flex items-center gap-2 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] px-5 py-2.5 text-xs uppercase tracking-widest"
        >
          <Plus size={14} /> Agregar miembro
        </button>
      )}
    </div>
  );
}
